import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  loginSuccess,
  logout,
  selectTokens,
  updateAccessToken,
  useAppDispatch,
  useAppSelector,
} from '@doccareplus/kernel/application';
import { apiClientFactory } from '@doccareplus/api-client';
import { authApi, configureAuthInterceptors } from '@doccareplus/kernel/infrastructure/api/auth.service';
import {
  applyActiveProfileHeader,
  clearPortalPatientData,
} from '../features/profiles';
import { ACTIVE_LINK_STORAGE_KEY } from '../features/profiles/domain/activeProfile';

// Portal counterpart of the clinic AuthSessionBridge. Wires the shared auth interceptors (token get/refresh/
// logout) and re-applies the selected patient profile as the X-Portal-Link-Id header on load. Unlike the clinic
// bridge it sets NO X-Clinic-Id / X-Active-Role — a portal account has no clinic context.
export function PortalAuthSessionBridge() {
  const dispatch = useAppDispatch();
  const tokens = useAppSelector(selectTokens);
  const queryClient = useQueryClient();

  // Re-apply the persisted active profile header on every reload so per-patient reads resolve to it.
  useEffect(() => {
    const activeLinkId = localStorage.getItem(ACTIVE_LINK_STORAGE_KEY);
    if (activeLinkId) {
      apiClientFactory.addHeader('X-Portal-Link-Id', activeLinkId);
    } else {
      apiClientFactory.removeHeader('X-Portal-Link-Id');
    }
  }, []);

  useEffect(() => {
    configureAuthInterceptors(
      () => tokens?.accessToken ?? null,
      async () => {
        if (!tokens?.accessToken || !tokens.refreshToken) {
          return null;
        }
        const refreshed = await authApi.refreshToken(tokens.accessToken, tokens.refreshToken);
        dispatch(loginSuccess({ user: refreshed.user, tokens: refreshed.tokens, memberships: refreshed.memberships }));
        dispatch(updateAccessToken(refreshed.tokens.accessToken));
        return refreshed.tokens.accessToken;
      },
      () => {
        applyActiveProfileHeader(null);
        void clearPortalPatientData(queryClient, { includeAccountData: true });
        dispatch(logout());
      }
    );
  }, [dispatch, queryClient, tokens]);

  return null;
}
